import {
    Card,
    createStyles,
    Grid,
    IconButton,
    Tooltip,
    withStyles,
    WithStyles,
} from '@material-ui/core';
import { AttachFile, Clear } from '@material-ui/icons';
import Search from '@material-ui/icons/Search';
import { FormikActions, FormikErrors, getIn } from 'formik';
import _ from 'lodash';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import Button from 'src/common/Button/Button';
import DatePickerField from 'src/common/DatePickerField/DatePickerField';
import ESDialog from 'src/common/ESDialog';
import FullWidthField from 'src/common/FullWidthField';
import SelectField from 'src/common/SelectField/SelectField';
import TypeAheadField from 'src/common/TypeAheadField/TypeAheadField';
import {
    DefaultActionItem,
    DefaultAttachmentFile,
    DefaultNote,
    IActionItem,
    IActionStatus,
    IAssessment,
    IBestPractice,
    IBranch,
    INote,
} from 'src/Definitions';
import {
    ActionItemAttachmentFileDto,
    ActionItemBestPracticeDto,
    ActionStatusDto,
    AttachmentFileDto,
    IAssessmentDto,
    IdentifierEntityDto,
    NoteDto,
} from 'src/generated_client';
import { IStores } from 'src/Stores';
import uuidv4 from 'uuid/v4';
import * as Yup from 'yup';
import Assignee from './Assignee/Assignee';
import NotesInlineList from './NotesInlineList';

const styles = () =>
    createStyles({
        attachmentFileCard: {
            color: 'black',
            margin: '5px',
            padding: '10px',
        },
        dialogGrid: {
            overflow: 'hidden',
        },
        noteGrid: {
            border: '1px',
            borderColor: 'lightgray',
            borderRadius: '4px',
            borderStyle: 'solid',
            height: '395px',
            overflowX: 'hidden',
            overflowY: 'auto',
        },
    });

type ISetFieldValue = (field: string, value: any) => void;

export interface IActionItemFormValues {
    name: string;
    dueDate: moment.Moment;
    assigneeId: string;
    assigneeName: string;
    assigneeEmail: string;
    statusId: string;
    status: ActionStatusDto;
    assessmentId: string;
    assessment: IdentifierEntityDto;
    description: string;
    assessmentCriteria: string;
    bestPracticeIds: string[];
    notes: NoteDto[];
    branchId: string;
}

const ActionItemSchema = Yup.object().shape({
    actionItem: Yup.object(),
    assessmentId: Yup.string().required('Assessment is required'),
    assigneeEmail: Yup.string().email('Not a valid email'),
    dueDate: Yup.string().required('Due Date is required'),
    statusId: Yup.string().required('Status is required'),
});

interface IActionItemDialogProps extends WithStyles<typeof styles> {
    actionItem?: IActionItem;
    archiveNote?: (noteId: string) => Promise<void>;
    assessments?: IAssessment[];
    attachmentFiles?: ActionItemAttachmentFileDto[];
    bestPractices?: IBestPractice[];
    branches?: IBranch[];
    close?: () => void;
    getAssessments?: (
        filter?: string,
        pageNumber?: number,
        pageSize?: number,
        showCompleted?: boolean,
        showVisitEnded?: boolean
    ) => Promise<void>;
    getBestPractices?: (filter?: string, pageNumber?: number, pageSize?: number) => Promise<void>;
    getBranches?: (pageNumber?: number, pageSize?: number) => Promise<void>;
    getStatuses?: (pageNumber?: number, pageSize?: number) => Promise<void>;
    isLoading?: boolean;
    isOpen?: boolean;
    saveActionItem?: (actionItem: IActionItem) => Promise<void>;
    saveNote?: (note: INote) => Promise<void>;
    sendNotificationEmail?: (actionItemId: string) => Promise<void>;
    setHasFileChanged?: (hasFileChanged: boolean) => void;
    statuses?: IActionStatus[];
}

class ActionItemDialog extends React.Component<IActionItemDialogProps> {
    public upload: HTMLInputElement | null;

    public state = {
        attachmentFiles: this.props.attachmentFiles,
        fileHasChanged: false,
        fileName: '',
        isFileLoading: false,
    };

    public save = async (
        values: IActionItemFormValues,
        formikBag: FormikActions<IActionItemFormValues>
    ) => {
        const actionItemToSave = {
            ...DefaultActionItem(),
            ...values,
            dueDate: values.dueDate ? moment(values.dueDate).toDate() : undefined,
            notes: values.notes,
        } as IActionItem;
        delete actionItemToSave.status;
        delete actionItemToSave.assessment;
        this.setBestPracticesToSave(actionItemToSave, values.bestPracticeIds);
        this.saveNotesToActionItem(actionItemToSave, values.notes);
        this.archiveNotes(values.notes, this.props.actionItem!.notes);
        await this.saveNotes(actionItemToSave.id, values.notes);
        this.props.saveActionItem!(actionItemToSave).finally(() => formikBag.setSubmitting(false));
    };

    public async saveNotes(actionItemId: string, notes: NoteDto[]) {
        notes.reverse();

        // Don't save things this way
        for (const note of notes) {
            const noteToSave = {
                ...DefaultNote(),
                ...note,
                actionItemId,
            } as INote;

            if (noteToSave.isNew) {
                await this.props.saveNote!(noteToSave);
            }
        }
    }

    public archiveNotes(newNotes: NoteDto[], originalNotes: NoteDto[]) {
        for (const note of originalNotes) {
            const foundNote = _.find(newNotes, (x) => note.id === x.id);
            if (foundNote === undefined) {
                this.props.archiveNote!(note.id!);
            }
        }
    }

    public sendNotificationEmail = (actionItem: IActionItem) => (
        _event: React.MouseEvent<HTMLElement>
    ) => {
        this.props.sendNotificationEmail!(actionItem.id);
    };

    private setBestPracticesToSave(actionItemToSave: IActionItem, bestPracticeIds: string[]) {
        actionItemToSave.bestPractices = [];
        this.getBestPracticesFromValues(bestPracticeIds).forEach((bestPractice) =>
            actionItemToSave.bestPractices.push(
                new ActionItemBestPracticeDto({
                    actionItemId: actionItemToSave.id,
                    bestPracticeId: bestPractice.id,
                })
            )
        );
    }

    private saveNotesToActionItem(actionItemToSave: IActionItem, notes: NoteDto[]) {
        actionItemToSave.notes = [];
        notes.forEach((note) => {
            const foundNote = _.find(this.props.actionItem!.notes, (x) => note.id === x.id);
            if (foundNote) {
                actionItemToSave.notes.push(note);
            } else {
                actionItemToSave.notes.push(
                    new NoteDto({
                        ...DefaultNote(),
                        ...note,
                    })
                );
            }
        });
    }

    public getAssessmentName(assessment: IAssessment) {
        return assessment.assessment || '';
    }

    public getAssessmentValue(assessment: IAssessment) {
        return assessment ? assessment.id : '';
    }

    public getBestPracticesFromValues(bestPracticeIds: string[]) {
        if (this.props.bestPractices) {
            return this.props.bestPractices.filter((bestPractice) =>
                bestPracticeIds.includes(bestPractice.id)
            );
        }
        return [];
    }

    public getBestPracticeName(bestPractice: Partial<IBestPractice>) {
        return bestPractice.name || '';
    }

    public getBestPracticeValue(bestPractice: Partial<IBestPractice>) {
        return bestPractice ? bestPractice.id || '' : '';
    }

    public getValuesFromBestPractices(bestPractices: ActionItemBestPracticeDto[] = []) {
        return bestPractices.map((x) => this.getBestPracticeValue(x.bestPractice!));
    }

    public getBranchFromValue(branchId: string) {
        if (this.props.branches) {
            return this.props.branches.find((branch) => branch.id === branchId);
        }
        return undefined;
    }

    public getBranchName(branch: IBranch) {
        return branch.name;
    }

    public getBranchValue(branch: IBranch) {
        return branch ? branch.id || '' : '';
    }

    public getStatusName(status: IActionStatus) {
        return status.name;
    }

    public getStatusValue(status: IActionStatus) {
        return status.id;
    }

    public componentDidMount() {
        this.props.getStatuses!(undefined, 5000);
        this.props.getBestPractices!('', undefined, 5000);
        this.props.getBranches!(undefined, 5000);
        this.props.getAssessments!('', undefined, 5000, true, true);
    }

    public deleteAttachmentFile = (
        setFieldValue: ISetFieldValue,
        attachmentFile: ActionItemAttachmentFileDto
    ) => (_event: React.MouseEvent<HTMLElement>) => {
        _.remove(
            this.props.actionItem!.actionItemAttachmentFiles,
            (x) => x.attachmentFileId === attachmentFile.attachmentFileId
        );
        setFieldValue('fileHasChanged', true);
        this.setState({});
    };

    public openFileLink = (actionItemAttachmentFileDto: ActionItemAttachmentFileDto) => () => {
        window.open(actionItemAttachmentFileDto.attachmentFile!.url, '_blank');
    };
    public handleOnButtonClick = (target: ActionItemDialog) => {
        return () => target.upload && target.upload.click();
    };

    public handleFileChange = (setFieldValue: ISetFieldValue) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
                setFieldValue('fileHasChanged', true);
                _.forEach(event.target.files, (file) => {
                    const reader = new FileReader();
                    reader.onloadstart = () => {
                        this.setState({
                            isFileLoading: true,
                        });
                    };
                    const attachmentFiles = this.props.actionItem!.actionItemAttachmentFiles;
                    reader.onload = () => {
                        if (reader) {
                            const newAttachmentFile = new AttachmentFileDto({
                                ...DefaultAttachmentFile(),
                                content: (reader.result! as string).split(',')[1],
                                mimeType: file.type.split('/')[1],
                                name: file.name,
                                url: reader.result as string,
                            });

                            const newActionItemAttachmentFile = new ActionItemAttachmentFileDto({
                                actionItemId: this.props.actionItem!.id,
                                attachmentFile: newAttachmentFile,
                                attachmentFileId: newAttachmentFile.id,
                                id: uuidv4(),
                                isActive: true,
                                name: this.props.actionItem!.name,
                            });
                            attachmentFiles!.push(newActionItemAttachmentFile);
                            this.props.actionItem!.isModified = true;
                            this.setState({
                                isFileLoading: false,
                            });
                            this.props.setHasFileChanged!(true);
                        }
                    };
                    reader.readAsDataURL(file);
                });
            }
        };
    };

    public render() {
        const { close, isLoading, isOpen, actionItem, statuses, branches } = this.props;

        const hasValue = (field: string, values: IActionItemFormValues) => {
            const value = getIn(values, field);

            return !!value;
        };

        const bestPracticeIds = actionItem
            ? this.getValuesFromBestPractices(actionItem.bestPractices)
            : [];
        return (
            <ESDialog
                close={close!}
                initialValues={
                    {
                        ...actionItem,
                        bestPracticeIds,
                    } as IActionItemFormValues
                }
                isLoading={isLoading}
                open={isOpen!}
                save={this.save}
                title={actionItem!.isNew ? 'Create Action' : 'Edit Action'}
                validationSchema={ActionItemSchema}
            >
                {({ errors, initialValues, isSubmitting, setFieldValue, values }) => {
                    return (
                        <Grid
                            container
                            spacing={24}
                            className={this.props.classes.dialogGrid}
                            direction="column"
                        >
                            <Grid container direction="row" spacing={24}>
                                <Grid item lg={9} md={7}>
                                    <Grid container spacing={16}>
                                        <Grid item xs={12} md={12}>
                                            <FullWidthField autoFocus name="name" label="Name" />
                                        </Grid>
                                        <Grid item xs={3} md={3}>
                                            <DatePickerField
                                                name="dueDate"
                                                inputId="dueDate"
                                                label="Due Date"
                                                variant="outlined"
                                                fullWidth
                                                required={true}
                                                errorMessage={errors.dueDate}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={6}>
                                            <Assignee
                                                actionItem={actionItem}
                                                errors={errors}
                                                initialValues={initialValues}
                                                setFieldValue={setFieldValue}
                                                values={values}
                                            />
                                        </Grid>
                                        <Grid item xs={3} md={3}>
                                            <SelectField
                                                name="statusId"
                                                label="Status"
                                                inputId="statusId"
                                                cypressLabel="statuses"
                                                childCypressLabel="status"
                                                variant="outlined"
                                                outlined
                                                required
                                                fullWidth
                                                items={statuses}
                                                getName={this.getStatusName}
                                                getValue={this.getStatusValue}
                                                errorMessage={errors.statusId}
                                                checkedItems={[values.statusId]}
                                                shrinkLabel={hasValue('statusId', values)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Grid container alignItems="flex-end" wrap="nowrap">
                                                <Grid item>
                                                    <Search />
                                                </Grid>
                                                <TypeAheadField
                                                    name="branchId"
                                                    label="Filter Assessment by Branch"
                                                    inputId="branch"
                                                    cypressLabel="branches"
                                                    childCypressLabel="branch"
                                                    fullWidth
                                                    selectedValue={this.getBranchFromValue(
                                                        values.branchId
                                                    )}
                                                    initialValue={this.getBranchFromValue(
                                                        initialValues.branchId
                                                    )}
                                                    items={branches}
                                                    getName={this.getBranchName}
                                                    getValue={this.getBranchValue}
                                                    setFieldValue={setFieldValue}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={6}>
                                            {this.renderAssessmentField(
                                                errors,
                                                initialValues.assessment,
                                                values.assessment,
                                                setFieldValue,
                                                this.getBranchFromValue(values.branchId)
                                            )}
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FullWidthField
                                                name="description"
                                                label="Description"
                                                fullWidth
                                                variant="outlined"
                                                rows={6}
                                                multiline
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FullWidthField
                                                name="acceptanceCriteria"
                                                label="Acceptance Criteria"
                                                fullWidth
                                                variant="outlined"
                                                rows={6}
                                                multiline
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            {this.renderBestPracticesField(
                                                { ...initialValues },
                                                { ...values },
                                                setFieldValue
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {!actionItem!.isNew ? (
                                    <Grid item lg={3} md={5} xs={12}>
                                        <Grid container>
                                            <Grid item xs={12} style={{ marginTop: '25px' }}>
                                                <NotesInlineList
                                                    noteGridStyle={this.props.classes.noteGrid}
                                                    setFieldValue={setFieldValue}
                                                    notes={[...values.notes]}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    disabled={actionItem!.isNew}
                                                    variant="contained"
                                                    onClick={this.sendNotificationEmail(
                                                        this.props.actionItem!
                                                    )}
                                                >
                                                    {'Send Notification Email'}
                                                </Button>
                                            </Grid>
                                            <Grid container>
                                                <Grid item>
                                                    <Button
                                                        component="label"
                                                        disabled={isSubmitting || isLoading}
                                                        isLoading={this.state.isFileLoading}
                                                        onClick={this.handleOnButtonClick(this)}
                                                        variant="contained"
                                                    >
                                                        Attach File
                                                    </Button>
                                                    <input
                                                        type="file"
                                                        onChange={this.handleFileChange(
                                                            setFieldValue
                                                        )}
                                                        ref={(ref) => (this.upload = ref)}
                                                        style={{
                                                            display: 'none',
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid container>
                                                    {actionItem!.actionItemAttachmentFiles!.map(
                                                        (x, index) => {
                                                            return (
                                                                <Grid item key={index}>
                                                                    <Card
                                                                        className={
                                                                            this.props.classes
                                                                                .attachmentFileCard
                                                                        }
                                                                    >
                                                                        <Grid
                                                                            container
                                                                            direction="row"
                                                                        >
                                                                            <Grid item>
                                                                                <AttachFile />
                                                                            </Grid>
                                                                            <Grid item>
                                                                                <div
                                                                                    style={{
                                                                                        cursor:
                                                                                            'pointer',
                                                                                    }}
                                                                                    onClick={this.openFileLink(
                                                                                        x
                                                                                    )}
                                                                                >
                                                                                    {
                                                                                        x.attachmentFile!
                                                                                            .name
                                                                                    }
                                                                                </div>
                                                                            </Grid>
                                                                            <Grid item>
                                                                                <Tooltip title="Delete Attachment">
                                                                                    <IconButton
                                                                                        style={{
                                                                                            height:
                                                                                                '24px',
                                                                                            padding: 0,
                                                                                            width:
                                                                                                '24px',
                                                                                        }}
                                                                                        onClick={this.deleteAttachmentFile(
                                                                                            setFieldValue,
                                                                                            x
                                                                                        )}
                                                                                    >
                                                                                        <Clear />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Card>
                                                                </Grid>
                                                            );
                                                        }
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Grid />
                                )}
                            </Grid>
                        </Grid>
                    );
                }}
            </ESDialog>
        );
    }

    private renderAssessmentField(
        errors: FormikErrors<IActionItemFormValues>,
        initialAssessment: IAssessmentDto,
        selectedAssessment: IAssessmentDto,
        setFieldValue: (field: string, value: any) => void,
        selectedBranch?: IBranch
    ) {
        const branchAssessments =
            selectedBranch && this.props.assessments
                ? this.props.assessments.filter(
                      (assessment) => assessment.targetBranch.id === selectedBranch.id
                  )
                : this.props.assessments;
        const initialAssessmentEntity = initialAssessment as IAssessment;
        const selectedAssessmentEntity = selectedAssessment as IAssessment;

        return (
            <TypeAheadField
                name="assessmentId"
                label="Assessment Search"
                inputId="assessment"
                cypressLabel="assessments"
                childCypressLabel="assessment"
                outlined
                fullWidth
                required
                initialValue={initialAssessmentEntity}
                items={branchAssessments}
                selectedValue={selectedAssessmentEntity}
                getName={this.getAssessmentName}
                getValue={this.getAssessmentValue}
                setFieldValue={setFieldValue}
                errorMessage={errors.assessmentId}
            />
        );
    }

    private renderBestPracticesField(
        initialValues: IActionItemFormValues,
        selectedValues: IActionItemFormValues,
        setFieldValue: (field: string, value: any) => void
    ) {
        return (
            <TypeAheadField
                name="bestPracticeIds"
                label="Best Practices Search"
                inputId="bestPractices"
                cypressLabel="bestPractices"
                childCypressLabel="bestPractice"
                fullWidth
                multiple
                outlined
                initialValue={this.getBestPracticesFromValues(initialValues.bestPracticeIds)}
                items={this.props.bestPractices}
                selectedValue={this.getBestPracticesFromValues(selectedValues.bestPracticeIds)}
                getName={this.getBestPracticeName}
                getValue={this.getBestPracticeValue}
                setFieldValue={setFieldValue}
            />
        );
    }
}

const InjectedActionItemDialog = inject<
    IStores,
    IActionItemDialogProps,
    Partial<IActionItemDialogProps>,
    any
>((stores: IStores) => ({
    actionItem: stores.actionItems.selectedActionItem,
    archiveNote: stores.actionItems.archiveNote,
    assessments: stores.assessments.assessments,
    bestPractices: stores.bestPractices.bestPractices,
    branches: stores.branches.branches,
    close: stores.actionItems.closeDialog,
    getAssessments: stores.assessments.getAllAssessments,
    getBestPractices: stores.bestPractices.getAllBestPractices,
    getBranches: stores.branches.getAllBranches,
    getStatuses: stores.actionStatuses.getAllActionStatuses,
    isLoading: stores.actionItems.isLoading,
    isOpen: stores.actionItems.isModalOpen,
    saveActionItem: stores.actionItems.saveActionItem,
    saveNote: stores.actionItems.saveNote,
    sendNotificationEmail: stores.actionItems.sendNotificationEmail,
    setHasFileChanged: stores.actionItems.setHasFileChanged,
    statuses: stores.actionStatuses.actionStatuses,
}))(withStyles(styles)(ActionItemDialog));

export default InjectedActionItemDialog;



// WEBPACK FOOTER //
// ./src/actionItems/ActionItemDialog.tsx